import { CalendarMonth, Timer } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../..";
import Colors from "../../../constants/Colors";
import { SchedulingService } from "../../../core/http/service/SchedulingService";
import { Paths } from "../../../enums/Paths";
import { Button } from "../../../pages/company/companyRegister/CompanyRegister.styled";
import * as collaboratorInitial from "../../../store/actions/collaboratorInitial";
import { resetSelectedTimeAvailable } from "../../../store/actions/scheduling";
import { ILogin } from "../../../store/reducers/login";
import { ISchedulingReducer } from "../../../store/reducers/scheduling";
import ErrorArea from "../../error/ErrorArea";
import {
  H1,
  SubTitle,
  SubTitleSimple,
  Text,
} from "../../UI/generalLayout/Layout.styled";
import { Spacer } from "../../UI/spacer/spacer";
import { MedicCardImage } from "../SchedulingCard.style";
import {
  ModalSchedulingButtonsContainer,
  ModalSchedulingCardContainer,
  ModalSchedulingHourCard,
  ModalSchedulingMedicCardContainer,
  ModalSchedulingSubTextContainer,
  ModalSchedulingTextCardContainer,
  ModalSchedulingTimeCardContainer,
  SchedulingCardModal,
  SchedulingCardModalContainer,
} from "./ModalSchedulingCard.style";

const convertDay = (day: string) => {
  const dayArray = day.split("-");
  return `${dayArray[2]}/${dayArray[1]}/${dayArray[0]}`;
};

export interface ModalSchedulingCardProps {
  useBackgroundColor?: boolean;
}

const ModalSchedulingCard: React.FC<ModalSchedulingCardProps> = ({
  useBackgroundColor = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [error, setError] = useState<any[]>([]);

  const {
    scheduling: {
      modalOpen = false,
      selectedTimeAvailable,
      selectedScheduleDay,
      selectedScheduleHour,
      consultType,
      pacienteId: pacienteIdOverride,
      empresaId: empresaIdOverride,
    },
    login: { userInfo, currentCompany },
  } = useSelector(
    (state: { scheduling: ISchedulingReducer; login: ILogin }) => state
  );

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const onConfirmSchedule = () => {
    const service = new SchedulingService();
    setLoading(true);

    try {
      service
      .postSchedule({
        pacienteId: pacienteIdOverride ?? userInfo?.id ?? "",
        empresaId: empresaIdOverride ?? currentCompany?.id ?? "",
        horarioId: selectedScheduleHour?.id ?? "",
        tipoConsulta: consultType ?? "Todos",
      })
      .then(async () => {
        if (pacienteIdOverride) {
          setSuccess(true);
          dispatch(collaboratorInitial.getResultsSchedulingPerson(pacienteIdOverride));
        } else {
          await dispatch(resetSelectedTimeAvailable());
          navigate(Paths.CollaboratorInitial);
        }
      })
      .catch((e) => {
        const errors: any[] = []
        e.response.data.errors.forEach((e: any) => {
          errors.push({ message: e })
        })
        setError(errors);
      })
      .finally(() => {
        setLoading(false);
      });
    } catch (error) {
      console.log(error)
    }

  };

  return (
    <SchedulingCardModal open={modalOpen} useBackgroundColor={false}>
      <SchedulingCardModalContainer>
        <ModalSchedulingCardContainer>
          {success ? (
            <ModalSchedulingTextCardContainer>
              <H1>Sucesso!</H1>
              <Text>Consulta agendada com sucesso.</Text>
              <Spacer bottom={30} />
              <ModalSchedulingButtonsContainer>
                <Button
                  onClick={async () => {
                    setSuccess(false);
                    await dispatch(resetSelectedTimeAvailable());
                  }}
                  color={Colors.WHITE}
                  backgroundColor={Colors.SPM600}
                >
                  OK
                </Button>
              </ModalSchedulingButtonsContainer>
            </ModalSchedulingTextCardContainer>
          ) : (
            <>
              <ModalSchedulingTextCardContainer>
                <H1>Confirmação de consulta</H1>
                <Text>
                  Verifique todos os dados abaixo e confirme sua consulta.{" "}
                </Text>
                <div style={{ width: "100%" }}>
                  <ErrorArea
                    errorList={error}
                    onCloseAction={() => {
                      setError([]);
                    }}
                  />
                </div>
              </ModalSchedulingTextCardContainer>

              <ModalSchedulingMedicCardContainer>
                <MedicCardImage
                  src={
                    selectedTimeAvailable?.foto ??
                    "https://via.placeholder.com/150"
                  }
                  alt={selectedTimeAvailable?.nome}
                />
                <div>
                  <Text color={Colors.SPM800} bold>
                    {selectedTimeAvailable?.nome}
                  </Text>
                  <SubTitleSimple color={Colors.GRAY700}>
                    {selectedTimeAvailable?.tipoProfissional}
                  </SubTitleSimple>
                </div>
              </ModalSchedulingMedicCardContainer>
              <ModalSchedulingTimeCardContainer>
                <ModalSchedulingHourCard>
                  <CalendarMonth />
                  <Text color={Colors.SPM600}>
                    {convertDay(selectedScheduleDay?.dia ?? "")}
                  </Text>
                </ModalSchedulingHourCard>
                <ModalSchedulingHourCard>
                  <Timer />
                  <Text color={Colors.SPM600}>
                    {selectedScheduleHour?.horario?.substring(0, 5)}
                  </Text>
                </ModalSchedulingHourCard>
              </ModalSchedulingTimeCardContainer>
              <Spacer bottom={22} />
              <ModalSchedulingSubTextContainer>
                <SubTitle>
                  Solicitamos comparecer à consulta com no mínimo 10 minutos de
                  antecedência. É necessária a apresentação de documento de
                  identidade e do crachá de identificação. Por favor, caso não
                  consiga comparecer a consulta, cancele pelo aplicativo.
                </SubTitle>
              </ModalSchedulingSubTextContainer>
              <Spacer bottom={30} />
              <ModalSchedulingButtonsContainer>
                <Button
                  onClick={async () => {
                    await dispatch(resetSelectedTimeAvailable());
                  }}
                  color={Colors.error}
                  backgroundColor={Colors.WHITE}
                  borderColor={Colors.error}
                >
                  Cancelar
                </Button>
                <Button
                  disabled={loading}
                  onClick={onConfirmSchedule}
                  color={Colors.WHITE}
                  backgroundColor={Colors.SPM600}
                >
                  {loading ? <CircularProgress /> : "Confirmar"}
                </Button>
              </ModalSchedulingButtonsContainer>
            </>
          )}
        </ModalSchedulingCardContainer>
      </SchedulingCardModalContainer>
    </SchedulingCardModal>
  );
};

export default ModalSchedulingCard;
